import { useState, useCallback } from 'react';
import { throttle } from 'throttle-debounce';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';
import { useMute } from '@/scenarios/legacy-player/hooks/useMute';
import { useInstanceCallback } from '@/scenarios/legacy-player/hooks/useInstanceCallback';
import { useInstanceEffect } from '@/scenarios/legacy-player/hooks/useInstanceEffect';

import { MIN_VOLUME, MAX_VOLUME } from '../constants/volume';

const VOLUME_CHANGE_THROTTLE = 20;
const isVolume = (volumeLevel: number): boolean =>
    volumeLevel >= MIN_VOLUME && volumeLevel <= MAX_VOLUME;

export const useVolume = () => {
    const instance = usePlayerInstance();
    const [volumeLevel, setVolumeLevel] = useState<number | undefined>(() => instance?.volume);
    const { isMuted, toggleMute } = useMute();

    const setVolume = useInstanceCallback(
        throttle(VOLUME_CHANGE_THROTTLE, true, (instance, volume: number) => {
            if (!instance) {
                return;
            }

            let newVolume = volume;

            if (volume <= MIN_VOLUME) {
                newVolume = MIN_VOLUME;
            }

            if (volume > MAX_VOLUME) {
                newVolume = MAX_VOLUME;
            }

            if (!isVolume(newVolume)) {
                return;
            }

            instance.setVolume(newVolume);

            if ((newVolume > MIN_VOLUME && isMuted) || (newVolume === MIN_VOLUME && !isMuted)) {
                toggleMute();
            }
        }),
        []
    );

    const changeVolumeByStep = useCallback(
        (step: number) => {
            if (!step || volumeLevel === undefined) {
                return;
            }

            if (step < 0 && isMuted) {
                return;
            }

            const fromValue = step > 0 && isMuted ? 0 : volumeLevel;

            setVolume(fromValue + step);
        },
        [setVolume, isMuted, volumeLevel]
    );

    useInstanceEffect((instance) => instance.onReady(() => setVolumeLevel(instance.volume)));
    useInstanceEffect((instance) => instance.onVolumeChange(setVolumeLevel));

    return { volume: volumeLevel, setVolume, changeVolumeByStep };
};
